/**
 * Shared bill-line draft partitioning for the two bill save paths
 * (BillQuickCreateDialog + BillDetailPage inline editor).
 *
 * The bug this fixes: both paths filtered lines to `description && account_id`
 * before POST/PUT. The bill line editor's account column is hidden by default
 * and account_id is only back-filled by the vendor AI suggestion, so any line
 * entered afterward was silently dropped from the request while the on-screen
 * footer totals still counted it — the dialog, the header total, and the DB all
 * disagreed.
 *
 * Rule: a line the user has TOUCHED (a description or a non-zero price) must
 * never be dropped silently. If it lacks a description or an expense account it
 * is "incomplete" — the caller blocks/holds the save and surfaces validation
 * instead of quietly discarding it.
 */

/** Minimal shape needed to decide whether a line was entered / is complete. */
export interface BillLineDraftLike {
  description: string;
  account_id: string;
  unit_price: number;
}

/** A line the user has interacted with (vs. the empty trailing add-row). */
export function isEnteredBillLine(line: BillLineDraftLike): boolean {
  return line.description.trim() !== '' || line.unit_price !== 0;
}

export interface PartitionedBillLines<T extends BillLineDraftLike> {
  /**
   * The entered lines with `fallbackAccountId` applied to any that had no
   * account of their own. These are the lines to persist.
   */
  entered: T[];
  /** An entered line still missing a description or an expense account. */
  hasIncomplete: boolean;
}

/**
 * Split lines into the entered set (with the fallback account applied) and a
 * flag for whether any entered line is still incomplete.
 *
 * @param lines             all editor lines, including the empty trailing row
 * @param fallbackAccountId account applied to entered lines without one (the
 *                          vendor AI suggestion in the create dialog; omitted on
 *                          the detail editor, which has a visible account column)
 */
export function partitionBillLines<T extends BillLineDraftLike>(
  lines: T[],
  fallbackAccountId?: string,
): PartitionedBillLines<T> {
  const entered = lines
    .filter(isEnteredBillLine)
    .map((line) =>
      line.account_id ? line : { ...line, account_id: fallbackAccountId ?? '' },
    );
  const hasIncomplete = entered.some(
    (line) => line.description.trim() === '' || !line.account_id,
  );
  return { entered, hasIncomplete };
}
